<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useNotifications } from '@/stores/notifications'
import { NotificationCategory } from '@/Support/Communications/Interfaces/NotificationData'
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/solid'
import { Timer } from '@/Support/Common/Functions/Timer'

const { notificationData, notificationBannerData, showNotification } =
  storeToRefs(useNotifications())

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'click'): void
}>()

const buttonClicked = async () => {
  emit('close')
  emit('click')
}

const closeNotification = () => {
  timer.pause()
  emit('close')
  pb.value = false
  showNotification.value = false
}

defineExpose({
  closeNotification,
})

/**
 * Timer
 */
let timer: Timer

watch(showNotification, newVal => {
  if (newVal) {
    setTimeout(() => (pb.value = true), 10)
    timer = new Timer(closeNotification, 5000)
  }
})

/**
 * Notification Categories
 */
const mainBg = computed(() => ({
  'bg-primary-600/70 dark:bg-primary-950/70':
    notificationData.value?.category === NotificationCategory.Success,
  'bg-tertiary-500/70 dark:bg-tertiary-800/70':
    notificationData.value?.category === NotificationCategory.Warning,
  'bg-tertiary-700/70 dark:bg-tertiary-950/70':
    notificationData.value?.category === NotificationCategory.Danger,
  'bg-secondary-500/70 dark:bg-secondary-800/70':
    notificationData.value?.category === NotificationCategory.Reminder,
  'bg-white/60 dark:bg-neutral-900/70':
    notificationData.value?.category === undefined ||
    notificationData.value?.category === NotificationCategory.Neutral,
}))

/**
 * Progress board
 */
const pb = ref(false)
const pbClass = computed(() => (pb.value ? 'scale-x-100' : 'scale-x-0'))
const pbHtml = ref<HTMLDivElement | null>(null)
const showClose = ref(false)
const pausePB = () => {
  showClose.value = true
  timer.pause()
  pb.value = false
}
const resumePB = () => {
  showClose.value = false
  timer.resume()
  pb.value = true
}
</script>
<template>
  <!-- Global notification live region, render this permanently at the end of the document -->
  <div
    v-if="notificationData"
    aria-live="assertive"
    class="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
  >
    <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
      <!-- Notification panel, dynamically insert this into the live region when it needs to be displayed -->
      <transition
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showNotification"
          class="pointer-events-auto flex flex-col w-full max-w-md rounded-lg backdrop-blur-xs shadow-lg ring-1 ring-black/5 text-neutral-100 dark:text-neutral-200 relative"
          :class="mainBg"
          @mouseover="pausePB"
          @mouseleave="resumePB"
        >
          <XMarkIcon
            v-if="showClose"
            class="w-5 h-5 absolute right-0 top-0 -mr-2 -mt-2 text-neutral-900 dark:text-neutral-100 p-1 rounded-full bg-neutral-200 ring-3 ring-neutral-900/10 hover:bg-neutral-50 dark:bg-neutral-950 dark:ring-neutral-800 dark:hover:bg-neutral-800"
            @click="closeNotification"
          />
          <div class="flex w-full max-w-md">
            <div class="w-0 flex-1 p-4">
              <div class="flex items-start">
                <div class="shrink-0 pt-0.5">
                  <ExclamationTriangleIcon
                    v-if="
                      notificationData.category === NotificationCategory.Danger
                    "
                    class="mt-1 w-8 h-8"
                  />
                  <ExclamationCircleIcon
                    v-else-if="
                      notificationData.category === NotificationCategory.Warning
                    "
                    class="mt-1 w-8 h-8"
                  />
                  <CheckCircleIcon
                    v-else-if="
                      notificationData.category === NotificationCategory.Success
                    "
                    class="mt-1 w-8 h-8"
                  />
                  <ClockIcon
                    v-else-if="
                      notificationData.category ===
                      NotificationCategory.Reminder
                    "
                    class="mt-1 w-8 h-8"
                  />
                  <InformationCircleIcon
                    v-else-if="!notificationData.owner"
                    class="mt-1 w-8 h-8"
                  />
                  <img
                    v-else
                    class="h-10 w-10 rounded-full"
                    :src="notificationData.owner?.profilePhotoUrl"
                    :alt="notificationData.owner?.name"
                  />
                </div>
                <div class="ml-3 w-0 flex-1">
                  <p
                    v-if="notificationData.owner"
                    class="text-sm font-medium text-neutral-900 dark:text-neutral-100"
                  >
                    {{ notificationData.owner.name }}
                  </p>
                  <p
                    v-if="notificationData.system || notificationData.title"
                    :class="{
                      'text-neutral-50': notificationData.system,
                      'text-neutral-800 dark:text-neutral-200 text-xs':
                        notificationData.title && !notificationData.system,
                    }"
                    class="text-sm font-medium"
                  >
                    {{ notificationData.title }}
                  </p>
                  <p
                    class="mt-1 text-sm font-light truncate"
                    :class="{
                      'text-neutral-100 dark:text-neutral-200':
                        notificationData.system,
                      'text-neutral-700 dark:text-neutral-200 text-xs':
                        !notificationData.system,
                    }"
                  >
                    {{ notificationData.content }}
                  </p>
                </div>
              </div>
            </div>
            <div
              v-if="notificationData.url"
              class="flex border-l border-neutral-400 dark:border-neutral-700"
            >
              <button
                type="button"
                class="flex w-full items-center justify-center rounded-none rounded-r-lg border border-transparent p-4 text-sm font-medium focus:outline-hidden focus:ring-2 focus:ring-primary-100 min-w-24 max-w-36"
                :class="
                  notificationData.system
                    ? 'text-primary-100 hover:text-primary-200'
                    : 'text-primary-700 hover:text-primary-800 dark:text-primary-200 dark:hover:text-primary-100'
                "
                @click="buttonClicked"
              >
                {{ __(notificationBannerData.buttonTitle) }}
              </button>
            </div>
          </div>

          <div
            ref="pbHtml"
            class="origin-top-left bg-neutral-500 border border-neutral-500 dark:border-neutral-400 opacity-25 transition w-full absolute duration-5000"
            :class="pbClass"
          ></div>
        </div>
      </transition>
    </div>
  </div>
</template>
